import { Box, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
  MUTED_MARK,
  STATUS_MARK,
  compact,
  isMoneyMeasure,
  money,
  num,
  statusPillColors,
} from './vizTokens';
import type { StatusTone } from './vizTokens';

export interface ChatStatTile {
  label: string;
  value: number | string;
  /** Value suffix, e.g. "%". */
  unit?: string;
  /** Signed change vs a named period. */
  delta?: number;
  /** What the delta is measured against, e.g. "vs last week". */
  delta_label?: string;
  /** Set false when a rise is bad (failures, arrears). Default true. */
  higher_is_better?: boolean;
  /** Optional sparkline series (<= ~12 points). */
  trend?: number[];
  tone?: StatusTone;
}

const MotionBox = motion(Box);

/**
 * Hand-authored sparkline — no chart library is installed in this app and none may be
 * added, so this is a plain `<path>` pair in a unit viewBox.
 *
 * `preserveAspectRatio="none"` lets the shape fill whatever width the tile has;
 * `vectorEffect="non-scaling-stroke"` keeps the stroke a true 2px despite the
 * non-uniform scale that would otherwise smear it.
 */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const points = data.map(num);
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const stepX = points.length > 1 ? 100 / (points.length - 1) : 100;
  const coords = points.map((v, i) => {
    const x = i * stepX;
    const y = 30 - ((v - min) / span) * 28;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const line = `M${coords.join(' L')}`;
  const area = `${line} L100,32 L0,32 Z`;

  return (
    <Box
      component="svg"
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      aria-hidden
      focusable="false"
      sx={{ display: 'block', width: '100%', height: 30 }}
    >
      <path d={area} fill={color} fillOpacity={0.14} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </Box>
  );
}

function DeltaBadge({
  delta,
  higherIsBetter,
  label,
}: {
  delta: number;
  higherIsBetter: boolean;
  label?: string;
}) {
  const theme = useTheme();
  const flat = Math.abs(delta) < 0.05;
  const good = higherIsBetter ? delta > 0 : delta < 0;
  const tone: StatusTone = flat ? 'neutral' : good ? 'success' : 'danger';
  const { bg, fg } = statusPillColors(theme, tone);
  const Icon = flat ? RemoveRoundedIcon : delta > 0 ? ArrowUpwardRoundedIcon : ArrowDownwardRoundedIcon;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.6, minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.6,
          py: 0.1,
          borderRadius: '6px',
          fontSize: '0.68rem',
          fontWeight: 700,
          bgcolor: bg,
          color: fg,
        }}
      >
        <Icon aria-hidden sx={{ fontSize: '0.75rem' }} />
        {flat ? 'flat' : `${delta > 0 ? '+' : ''}${compact(delta)}`}
      </Box>
      {label && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.66rem' }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

/**
 * A KPI row — the right form for a handful of headline numbers, and the reason the
 * prompt forbids a one-bar bar chart.
 *
 * Identity comes from a coloured rail beside the text, never from coloured text: at
 * 13px the portal's body size, tinted copy fails contrast in one mode or the other.
 */
export function ChatStatTiles({ tiles }: { tiles: ChatStatTile[] }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const items = (tiles || []).filter((t) => t && t.label != null);
  if (!items.length) return null;

  return (
    <Box
      sx={{
        my: 1.25,
        display: 'grid',
        gap: 1,
        gridTemplateColumns: {
          xs: 'repeat(auto-fit, minmax(128px, 1fr))',
          sm: 'repeat(auto-fit, minmax(146px, 1fr))',
        },
      }}
    >
      {items.map((t, i) => {
        const tone: StatusTone = t.tone ?? 'neutral';
        const accent = tone === 'neutral' ? MUTED_MARK : STATUS_MARK[tone];
        // Money keeps every digit and carries its own symbol, so the currency `unit`
        // is not printed a second time beside it.
        const isMoney = isMoneyMeasure(t.unit, t.label);
        const value =
          typeof t.value === 'number'
            ? isMoney
              ? money(t.value, t.unit)
              : compact(t.value)
            : String(t.value ?? '—');
        return (
          <MotionBox
            key={`${t.label}-${i}`}
            initial={{ opacity: 0, y: reduceMotion ? 0 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: reduceMotion ? 0 : 0.26,
              delay: reduceMotion ? 0 : i * 0.045,
              ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              minWidth: 0,
              pl: 1.4,
              pr: 1.1,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px', // literal px: `sx.borderRadius` is theme-multiplied
              bgcolor: alpha(theme.palette.text.primary, isLight ? 0.018 : 0.05),
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                insetBlock: 0,
                left: 0,
                width: '3px',
                bgcolor: accent,
              }}
            />
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: 'block',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {t.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.15 }}>
              <Box
                component="span"
                sx={{
                  fontSize: isMoney && value.length > 11 ? '1.05rem' : '1.25rem',
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: 'text.primary',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {value}
              </Box>
              {t.unit && !isMoney && (
                <Box
                  component="span"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}
                >
                  {t.unit}
                </Box>
              )}
            </Box>
            {typeof t.delta === 'number' && (
              <DeltaBadge
                delta={t.delta}
                higherIsBetter={t.higher_is_better !== false}
                label={t.delta_label}
              />
            )}
            {Array.isArray(t.trend) && t.trend.length > 1 && (
              <Box sx={{ mt: 0.75 }}>
                <Sparkline data={t.trend.slice(0, 12)} color={accent} />
              </Box>
            )}
          </MotionBox>
        );
      })}
    </Box>
  );
}
